import type { Session } from '@supabase/supabase-js'
import { useEffect, useState } from 'react'
import { Layout } from './components/Layout'
import { AuthGate } from './components/AuthGate'
import { answerFollowup, claimInvitation, getCurrentMembership, inviteViewer, listMembers, loadCloudData, loadViewerData, proposeSource, revokeMember, saveDecision, saveSettings, saveWeeklyContext, setSourceStatus, submitInsights } from './lib/cloud'
import { demoData } from './lib/demo'
import { adminEmail, cloudConfigured, supabase } from './lib/supabase'
import { HDMPage } from './pages/HDMPage'
import { HealthPage } from './pages/HealthPage'
import { HomePage } from './pages/HomePage'
import { ReportsPage } from './pages/ReportsPage'
import { SettingsPage } from './pages/SettingsPage'
import { SetupPage } from './pages/SetupPage'
import { SourcesPage } from './pages/SourcesPage'
import { TrendsPage } from './pages/TrendsPage'
import type { AppData, PageKey } from './types'

const validPages: PageKey[] = ['home', 'trends', 'hdm', 'sources', 'reports', 'settings', 'health', 'setup']

export default function App() {
  const initialHash = window.location.hash.replace('#/', '') as PageKey
  const [page, setPageState] = useState<PageKey>(validPages.includes(initialHash) ? initialHash : 'home')
  const [session, setSession] = useState<Session | null>(null)
  const [accessRole, setAccessRole] = useState<'owner' | 'editor' | 'viewer' | null>(null)
  const [authReady, setAuthReady] = useState(!cloudConfigured)
  const [data, setData] = useState<AppData>(demoData)
  const [cloudError, setCloudError] = useState('')
  const setPage = (next: PageKey) => {
    const allowed = accessRole === 'viewer' ? 'reports' : next
    setPageState(allowed); window.location.hash = `/${allowed}`
  }

  useEffect(() => {
    const handler = () => {
      const next = window.location.hash.replace('#/', '') as PageKey
      if (accessRole === 'viewer') {
        if (next !== 'reports') window.location.hash = '/reports'
        setPageState('reports')
      } else if (validPages.includes(next)) setPageState(next)
    }
    window.addEventListener('hashchange', handler)
    return () => window.removeEventListener('hashchange', handler)
  }, [accessRole])

  useEffect(() => {
    if (!supabase) return
    supabase.auth.getSession().then(({ data: auth }) => { setSession(auth.session); setAuthReady(true) })
    const { data: listener } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      if (!next) setAccessRole(null)
      setAuthReady(true)
    })
    return () => listener.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!session) return
    setCloudError('')
    claimInvitation()
      .then(getCurrentMembership)
      .then(async (member) => {
        setAccessRole(member.role)
        if (member.role === 'viewer') {
          setPageState('reports')
          window.location.hash = '/reports'
          setData(await loadViewerData())
        } else {
          setData(await loadCloudData())
        }
      })
      .catch((error) => setCloudError(error.message ?? 'Cloud data could not be loaded.'))
  }, [session])

  if (!authReady) return <main className="auth-screen"><div className="loading-state">Loading secure access…</div></main>
  if (cloudConfigured && !session) return <AuthGate />
  if (cloudConfigured && session && !accessRole) return <main className="auth-screen"><section className="auth-card"><span className="eyebrow">HDM Trend Intelligence</span><h1>{cloudError ? 'Access unavailable' : 'Checking access...'}</h1><p>{cloudError || 'Confirming this email and its permitted dashboard role.'}</p>{cloudError && <button className="button primary" onClick={() => supabase?.auth.signOut()}>Return to sign in</button>}</section></main>

  const content = {
    home: <HomePage data={data} setPage={setPage} />,
    trends: <TrendsPage trends={data.trends} onDecision={(trendId, decision) => saveDecision(trendId, decision, session?.user.email ?? adminEmail)} />,
    hdm: <HDMPage beliefs={data.beliefs} questions={data.questions} hdmPosts={data.hdmPosts} onAnswer={cloudConfigured ? answerFollowup : undefined} onInsights={cloudConfigured ? submitInsights : undefined} onContext={cloudConfigured ? saveWeeklyContext : undefined} />,
    sources: <SourcesPage initialSources={data.sources} onPropose={cloudConfigured ? proposeSource : undefined} onStatus={cloudConfigured ? setSourceStatus : undefined} />,
    reports: <ReportsPage reports={data.reports} />,
    settings: <SettingsPage initial={data.settings} onSave={cloudConfigured ? saveSettings : undefined} onInvite={cloudConfigured ? inviteViewer : undefined} onListMembers={cloudConfigured ? listMembers : undefined} onRevokeMember={cloudConfigured ? revokeMember : undefined} />,
    health: <HealthPage data={data} />,
    setup: <SetupPage health={data.health} />,
  }[page]

  return <Layout page={page} setPage={setPage} cloud={cloudConfigured} viewerOnly={accessRole === 'viewer'}>
    {!cloudConfigured && <div className="mode-ribbon"><span aria-hidden="true">✓</span> Interactive preview · no cloud or external calls</div>}
    {cloudError && <div className="mode-ribbon error">Cloud connection needs attention: {cloudError}</div>}
    {content}
  </Layout>
}
